import { AudioEngine } from './AudioEngine';

/**
 * GameMonetize HTML5 SDK wrapper. The bootstrap (window.SDK_OPTIONS + api.gamemonetize.com/sdk.js)
 * is injected into index.html at build time for the GameMonetize variant (our hosting + itch.io).
 * That bootstrap's onEvent forwards every event name to window.__gmEvent, which we install here.
 *
 * The SDK exposes a single ad trigger, `sdk.showBanner()`, plus three lifecycle events:
 *   SDK_READY        — SDK initialised
 *   SDK_GAME_PAUSE   — an ad is starting; pause + MUTE (background audio over ads is forbidden)
 *   SDK_GAME_START   — the ad finished (or none filled); resume + unmute
 * There is no distinct rewarded-completion callback, so a rewarded reward is granted when the ad
 * flow completes — the standard pattern for this SDK.
 */
interface GMSdk { showBanner?: () => void }
function gmsdk(): GMSdk | null {
  return (window as unknown as { sdk?: GMSdk }).sdk ?? null;
}

export class GameMonetize {
  private static pending: (() => void) | null = null;
  private static prevMuted = false;
  private static timer: number | null = null;

  /** Hook the SDK's global event stream (set up by the index.html bootstrap). */
  static init(): Promise<void> {
    (window as unknown as { __gmEvent?: (name: string) => void }).__gmEvent = (name) => GameMonetize.onEvent(name);
    return Promise.resolve();
  }

  private static onEvent(name: string): void {
    if (name === 'SDK_GAME_PAUSE') { this.prevMuted = AudioEngine.muted; AudioEngine.setMuted(true); return; }
    if (name === 'SDK_GAME_START') { AudioEngine.setMuted(this.prevMuted); this.resolve(); return; }
  }

  private static resolve(): void {
    if (this.timer !== null) { clearTimeout(this.timer); this.timer = null; }
    const cb = this.pending; this.pending = null;
    cb?.();
  }

  /** Show an ad; `done` runs when it finishes — or immediately if no SDK / no fill / error. */
  private static show(done: () => void): void {
    const s = gmsdk();
    if (!s || typeof s.showBanner !== 'function') { done(); return; }
    this.pending = done;
    // If the SDK never fires SDK_GAME_START (no fill / blocked), don't leave the game hanging.
    this.timer = window.setTimeout(() => { AudioEngine.setMuted(this.prevMuted); this.resolve(); }, 20000);
    try { s.showBanner(); } catch { this.resolve(); }
  }

  /** Interstitial at a natural break. `onDone` always runs at the end. */
  static interstitial(onDone?: () => void): void { this.show(() => onDone?.()); }

  /** Rewarded: `onReward` is granted on ad completion; `onClose` always runs at the end. */
  static rewarded(onReward: () => void, onClose?: () => void): void {
    this.show(() => { onReward(); onClose?.(); });
  }
}
